// Resumo por categoria das peças da Ficha da OS: o que cada categoria
// recolhida mostra no cabeçalho — números e situação — e se ela abre sozinha.
// Puro (sem DOM). Os números de cada peça vêm da Prévia do Fechamento
// (`PreviaTipo.conta`, #91) — aqui só se soma; "sem destino" e "o que a
// Comanda rende" são contas do domínio.

import type { Id } from "../../domain/ids.ts";
import type { ImpedimentoRetencao } from "../../domain/situacao-retencao.ts";

/**
 * O que impede o "Fechar OS" numa linha, em ordem de prioridade: motivo de
 * divergência não escolhido, carona acima do que existia no Backlog
 * (ADR-0027/0028), Retida digitada diferente do Backlog calculado, peça
 * retida sem motivo de retenção (ADR-0029) e qualquer outra recusa da
 * Prévia do Fechamento na peça.
 */
export type Impedimento = "motivo" | "backlog" | Exclude<ImpedimentoRetencao, "semDevolvida"> | "outro";

export interface ItemCategoria {
  recebida: number;
  /** O que a Comanda rende (Conferida, senão Recebida) — da Prévia. */
  rende: number;
  /** Total digitado em Devolvida (parte própria + carona do Backlog). */
  devolvida: number;
  retidas: number;
  /** Peças da Comanda que nem voltaram nem ficaram — da Prévia. */
  semDestino: number;
  impedimento: Impedimento | null;
}

export interface ResumoCategoria {
  recebida: number;
  /** O que as Comandas rendem: Conferida quando houver, senão a Recebida. */
  conferida: number;
  devolvida: number;
  retida: number;
  /** Peças da Comanda que nem voltaram nem ficaram retidas. */
  semDestino: number;
  /** O impedimento mais urgente entre as linhas; `null` sem nenhum. */
  impedimento: Impedimento | null;
}

export type TomSituacao = "neutro" | "atencao" | "ok";

export interface SituacaoCategoria {
  texto: string;
  tom: TomSituacao;
}

const PRIORIDADE: readonly Impedimento[] = ["motivo", "backlog", "retida", "semMotivo", "outro"];

export function resumirCategoria(itens: readonly ItemCategoria[]): ResumoCategoria {
  const r: ResumoCategoria = { recebida: 0, conferida: 0, devolvida: 0, retida: 0, semDestino: 0, impedimento: null };
  for (const i of itens) {
    r.recebida += i.recebida;
    r.conferida += i.rende;
    r.devolvida += i.devolvida;
    r.retida += i.retidas;
    r.semDestino += i.semDestino;
    if (i.impedimento && (r.impedimento === null || PRIORIDADE.indexOf(i.impedimento) < PRIORIDADE.indexOf(r.impedimento))) {
      r.impedimento = i.impedimento;
    }
  }
  return r;
}

/** A categoria tem alguma peça nesta OS (recebida, devolvida ou retida). */
export function temPeca(r: ResumoCategoria): boolean {
  return r.recebida > 0 || r.devolvida > 0 || r.retida > 0;
}

const TEXTO_IMPEDIMENTO: Record<Impedimento, string> = {
  motivo: "Falta Motivo",
  backlog: "Falta no Backlog",
  retida: "Retida Diferente",
  semMotivo: "Retida sem Motivo",
  // outra recusa do "Fechar OS" na peça (Prévia do Fechamento, #84)
  outro: "Não Fecha",
};

/**
 * A situação da categoria, na ordem do que pede ação primeiro: o que impede o
 * "Fechar OS", depois peça da Comanda sem destino.
 */
export function situacaoCategoria(r: ResumoCategoria): SituacaoCategoria {
  if (r.impedimento) return { texto: TEXTO_IMPEDIMENTO[r.impedimento], tom: "atencao" };
  if (!temPeca(r)) return { texto: "Sem Peças", tom: "neutro" };
  if (r.devolvida === 0 && r.retida === 0) return { texto: "A Fechar", tom: "neutro" };
  if (r.semDestino > 0) return { texto: `${r.semDestino} Sem Destino`, tom: "atencao" };
  return { texto: "Fecha", tom: "ok" };
}

/**
 * Se a categoria começa aberta quando a Ficha monta. Com a Coleta começando
 * (nenhuma peça lançada na OS ainda — a tabela só aparece depois de salvar a
 * Coleta): tem peça cadastrada para o Cliente, onde a comanda vai ser
 * digitada (decisão do dono). Senão: tem peça nesta OS ou algo impede o
 * fechamento.
 */
export function abreNaMontagem(
  r: ResumoCategoria,
  contexto: { coletaComecando: boolean; temPecaDoCliente: boolean },
): boolean {
  if (contexto.coletaComecando) return contexto.temPecaDoCliente;
  return temPeca(r) || r.impedimento !== null;
}

export interface GrupoCategoria {
  categoriaId: Id;
  nome: string;
  /** Índices na lista de peças, na ordem em que vieram. */
  indices: number[];
}

/** Agrupa as peças por categoria, categorias em ordem alfabética. */
export function agruparPorCategoria(
  pecas: readonly { categoriaId: Id }[],
  nomeDaCategoria: (id: Id) => string,
): GrupoCategoria[] {
  const grupos = new Map<Id, GrupoCategoria>();
  pecas.forEach((p, indice) => {
    const g = grupos.get(p.categoriaId) ?? { categoriaId: p.categoriaId, nome: nomeDaCategoria(p.categoriaId), indices: [] };
    g.indices.push(indice);
    grupos.set(p.categoriaId, g);
  });
  return [...grupos.values()].sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"));
}
